// @flow
import ModularUIRequest from "../../modularui/ModularUIRequest";
import { HTTP_METHODS, MODULARUI_STATUS } from "../../constants/Constants";

import { startProgress, finishProgress } from "../actions/ProgressIndicator";
import { handleError } from "../actions/Error";

import { updateStatus } from "./ModularUIActions";

import type { Middleware, MiddlewareAPI } from "redux";
import type {
  ReduxAction,
  ReduxState,
  Dispatch,
  PossibleAction,
} from "../types";
import type { ModularUIModel } from "../../models/types";
import type Href from "../../models/href/Href";
import type { ModularUIAction, SuccessAction, ErrorAction } from "./types";
import type { TargetModel } from "../../modularui/types";

type RequestOptions = {
  origin?: string,
  contextPath?: string,
  href: Href,
  method?: $Keys<typeof HTTP_METHODS>,
  params?: string,
  data?: any,
  timeout?: number,
  locale: string,
  childmodels?: boolean,
  targetModel?: TargetModel,
  forceTargetModel?: boolean,
  isReload?: boolean,
  cache?: boolean,
  headers?: {
    [headerName: string]: string,
    Accept?: string,
    "Accept-Language"?: string,
    "Content-Type"?: string,
    "x-filename"?: string,
    "x-filesize"?: string,
  },
  events?: { [eventName: string]: () => void },
  onProgress?: ProgressEventHandler,
  withCredentials?: boolean,
};

/**
 * Symbol key that carries API call info interpreted by this Redux middleware.
 */
const createRequest = (modularui: RequestOptions): ModularUIRequest => {
  const request = new ModularUIRequest(modularui.href, {
    method: modularui.method || HTTP_METHODS.GET,
    params: modularui.params || "",
    data: modularui.data || {},
    locale: modularui.locale,
    childmodels: modularui.childmodels ?? true,
    isReload: modularui.isReload,
    cache: modularui.cache,
    headers: modularui.headers,
    events: modularui.events,
    onProgress: modularui.onProgress,
  });

  if (modularui.origin) {
    request.origin = modularui.origin;
  }

  if (modularui.contextPath) {
    request.contextPath = modularui.contextPath;
  }

  if (modularui.targetModel) {
    request.targetModel = modularui.targetModel;
    request.forceTargetModel = modularui.forceTargetModel ?? false;
  }

  return request;
};

/**
 */
const responseHandler = (
  next: Dispatch,
  dispatch: Dispatch,
  successAction: SuccessAction,
  model: ModularUIModel,
) => {
  if (successAction) {
    const successResult = successAction(model);

    if (successResult instanceof Promise) {
      successResult
        .then((result) => {
          dispatch(result);
        })
        .catch((error) => {
          next(handleError(error));
        });
    } else {
      try {
        dispatch(successResult);
      } catch (error) {
        throw new Error(
          `Result of successResult is not a valid redux action: ${error}`,
        );
      }
    }
  }

  return next(finishProgress());
};

/**
 */
const errorHandler = (
  next: Dispatch,
  dispatch: Dispatch,
  errorAction: ?ErrorAction,
  err: any,
) => {
  dispatch(finishProgress());

  if (errorAction) {
    const errorResult = errorAction(err);

    if (errorResult instanceof Promise) {
      errorResult.then((result) => dispatch(result));
    } else {
      dispatch(errorResult);
    }
  }

  return next(handleError(err));
};

/**
 */
const handleFetch = (
  action: ModularUIAction,
  locale: string,
  dispatch: Dispatch,
  next: Dispatch,
) => {
  dispatch(startProgress());

  const { key, successAction, errorAction, ...requestOptions } = action.payload;
  requestOptions.locale = locale;

  const modularuiRequest = createRequest(requestOptions);

  dispatch(updateStatus(key, MODULARUI_STATUS.LOADING, requestOptions));

  return modularuiRequest
    .fetch()
    .then((model) => responseHandler(next, dispatch, successAction, model))
    .catch((error) => errorHandler(next, dispatch, errorAction, error));
};

/**
 */
export const modularUIMiddleware: Middleware<
  ReduxState,
  ReduxAction,
  Dispatch,
> =
  (api: MiddlewareAPI<ReduxState, ReduxAction, Dispatch>) =>
  (next: Dispatch) =>
  (action: PossibleAction) => {
    if (action.type === "MODULARUI/FETCH") {
      return handleFetch(
        // $FlowExpectedError[incompatible-exact]
        action,
        api.getState().i18n.locale,
        api.dispatch,
        next,
      );
    }

    return next(action);
  };
